import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Extension } from "@tiptap/core";
import { ReactRenderer } from "@tiptap/react";
import Suggestion from "@tiptap/suggestion";
import { filterBlocks, runSlash } from "@/components/admin/posts/blocks";
import { cn } from "@/lib/utils";

const SlashList = forwardRef(({ items, command, rect }, ref) => {
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [items]);
  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") { setIndex((i) => (i + items.length - 1) % Math.max(items.length, 1)); return true; }
      if (event.key === "ArrowDown") { setIndex((i) => (i + 1) % Math.max(items.length, 1)); return true; }
      if (event.key === "Enter") { if (items[index]) command(items[index]); return true; }
      return false;
    },
  }));
  const h = 56 + Math.min(items.length, 6) * 52;
  const below = rect ? rect.bottom + 8 + h <= window.innerHeight : true;
  const top = rect ? (below ? rect.bottom + 8 : Math.max(8, rect.top - h - 8)) : 0;
  const left = rect ? Math.min(rect.left, window.innerWidth - 336) : 0;
  return (
    <div className="hk-slash" style={{ position: "fixed", top, left, zIndex: 80, maxHeight: h }} role="listbox" aria-label="Set inn blokk" data-testid="slash-menu">
      <p className="hk-slash-head">Set inn blokk</p>
      {items.length ? items.map((b, i) => (
        <button key={b.id} type="button" role="option" aria-selected={i === index} onMouseEnter={() => setIndex(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => command(b)} className={cn("hk-slash-item", i === index && "is-active")} data-testid={`slash-item-${b.id}`}>
          <span className="hk-slash-icon"><b.icon className="h-4 w-4" /></span>
          <span className="min-w-0"><span className="block text-[13.5px] font-medium">{b.label}</span><span className="block truncate text-[12px] text-muted-foreground">{b.hint}</span></span>
        </button>
      )) : <p className="px-3 py-2 text-[13px] text-muted-foreground">Ingen blokk heiter det</p>}
    </div>
  );
});

/** Type «/» in an empty line → block menu (arrows + Enter, or click). */
export const SlashCommand = Extension.create({
  name: "slashCommand",
  addProseMirrorPlugins() {
    return [Suggestion({
      editor: this.editor,
      char: "/",
      allowSpaces: false,
      startOfLine: false,
      items: ({ query }) => filterBlocks(query),
      command: ({ editor, range, props }) => runSlash(editor, range, props.id),
      render: () => {
        let component;
        const place = (props) => component?.updateProps({ ...props, rect: props.clientRect?.() });
        return {
          onStart: (props) => { component = new ReactRenderer(SlashList, { props: { ...props, rect: props.clientRect?.() }, editor: props.editor }); document.body.appendChild(component.element); },
          onUpdate: place,
          onKeyDown: (props) => { if (props.event.key === "Escape") { component?.destroy(); component?.element?.remove(); component = null; return true; } return component?.ref?.onKeyDown(props) ?? false; },
          onExit: () => { component?.element?.remove(); component?.destroy(); component = null; },
        };
      },
    })];
  },
});
